import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, LinearProgress, Paper, Stack, Table, TableBody,
  IconButton, TableCell, TableHead, TableRow, ThemeProvider, Tooltip, Typography,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EventIcon from '@mui/icons-material/Event';
import { Link } from 'react-router-dom';

import { getStudentStats } from '../../actions/journal';
import { journalGroupLink, journalSheetLink } from '../../utils/consts/pathRoutes';
import { formatDate } from '../../utils/journalFormat';
import { PERMISSIONS, useCurrentUser } from '../../utils/currentUser';
import { hasJournalAccess } from '../../utils/consts/roles';
import { journalTheme } from '../../utils/consts/journalTheme';
import MembershipPeriodModal from '../../Pages/Journal/MembershipPeriodModal';
import StudentGroupAssign from './StudentGroupAssign';
import StudentFiles from './StudentFiles';
import StudentComments from './StudentComments';
import './journal.css';

// Ниже этого процента заказчик просил связываться со слушателем.
const ALERT_THRESHOLD = 50;

/**
 * Блок «Электронный журнал» в карточке слушателя.
 *
 * Показывает группы, куратора и посещаемость. Статистика считается по слушателю в целом,
 * а не в рамках одного журнала: при переводе проценты должны складываться, а не начинаться
 * заново (требование ТЗ 3.6).
 */
const StudentJournalBlock = ({ studentId, embedded = false }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPeriod, setEditingPeriod] = useState(null);

  const { can } = useCurrentUser();
  const canManage = can(PERMISSIONS.JOURNAL_MANAGE);

  useEffect(() => {
    if (!studentId || !hasJournalAccess()) {
      setLoading(false);
      return;
    }

    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  function load() {
    setLoading(true);
    getStudentStats(studentId)
      .then(setStats)
      .catch(() => setError('Не удалось загрузить данные журнала'))
      .finally(() => setLoading(false));
  }

  if (!hasJournalAccess()) return null;

  const active = (stats?.periods || []).filter((period) => period.is_active);

  const content = (
    <Box>
      {!embedded && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <MenuBookIcon sx={{ color: '#5a5a5a' }} />
          <span className="journal-title" style={{ fontSize: 20 }}>Электронный журнал</span>
        </Stack>
      )}

      <StudentGroupAssign
        studentId={studentId}
        periods={stats?.periods || []}
        onAssigned={load}
      />

      {loading && <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={24} color="warning" /></Stack>}

      {error && <Alert severity="warning">{error}</Alert>}

      {!loading && !error && stats && (
        <Box>
          {stats.periods.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Слушатель не распределён ни в одну группу.
            </Typography>
          ) : (
            <>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2">
                  Текущая группа:{' '}
                  {active.length === 0
                    ? <span style={{ color: '#b26a00' }}>нет</span>
                    : active.map((period) => (
                      <Link
                        key={period.membership_id}
                        to={journalGroupLink(period.group_id)}
                        className="journal-link" style={{ marginRight: 8 }}
                      >
                        {period.group_name}
                      </Link>
                    ))}
                </Typography>

                {active[0]?.curator && (
                  <Chip size="small" variant="outlined"
                    label={`Куратор: ${active[0].curator.name}`} />
                )}

                {active.length > 0 && (
                  <Link className="journal-link" to={journalSheetLink(active[0].group_id)}>
                    Открыть журнал группы
                  </Link>
                )}
              </Stack>

              <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  Часов посещено: <b>{stats.total.attended_hours}</b> из {stats.total.planned_hours}
                </Typography>
                <Typography variant="body2">
                  Пропуски: {stats.total.absences.unexcused} без причины ·{' '}
                  {stats.total.absences.excused} по уважительной ·{' '}
                  {stats.total.absences.medical} по справке
                </Typography>
              </Stack>

              {stats.total.percent !== null && (
                <Box sx={{ mb: 2, maxWidth: 420 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption">Посещаемость</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {stats.total.percent} %
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(stats.total.percent, 100)}
                    color={stats.total.below_threshold ? 'error' : 'success'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  {stats.total.below_threshold && (
                    <Typography variant="caption" color="error">
                      Ниже {ALERT_THRESHOLD} % — повод связаться со слушателем
                    </Typography>
                  )}
                </Box>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Группа</TableCell>
                    <TableCell>Период</TableCell>
                    <TableCell>Куратор</TableCell>
                    <TableCell align="center">Часы</TableCell>
                    <TableCell align="center">Статус</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.periods.map((period) => (
                    <TableRow key={period.membership_id}>
                      <TableCell>
                        <Link className="journal-link" to={journalGroupLink(period.group_id)}>
                          {period.group_name}
                        </Link>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {period.semester} семестр · {period.academic_year}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatDate(period.date_from)}
                        {period.date_to ? ` — ${formatDate(period.date_to)}` : ' — по настоящее время'}
                        {canManage && (
                          <Tooltip title="Изменить даты обучения в группе">
                            <IconButton size="small" sx={{ ml: 0.5 }} onClick={() => setEditingPeriod(period)}>
                              <EventIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{period.curator?.name || '—'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={period.hours_carried_over
                          ? `Принесено из прошлой группы: ${period.hours_carried_over} ч`
                          : 'Часы, набранные в этой группе'}
                        >
                          <span>
                            {period.attended_hours} / {period.planned_hours}
                            {period.hours_carried_over > 0 && ` (+${period.hours_carried_over})`}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={period.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Box>
      )}

      <MembershipPeriodModal
        membership={editingPeriod}
        onClose={() => setEditingPeriod(null)}
        onSaved={() => { setEditingPeriod(null); load(); }}
      />

      <StudentComments studentId={studentId} />

      <StudentFiles studentId={studentId} />
    </Box>
  );

  return embedded ? content : (
    <ThemeProvider theme={journalTheme}>
      <Paper sx={{ p: 2, mb: 2 }}>{content}</Paper>
    </ThemeProvider>
  );
};

export default StudentJournalBlock;
